import React, { useRef, useState } from 'react';
import Icon from '../Icon';
import './ui.css';

/*
 * The shared pieces of the Yön B design (Faz 23, T-180; b.css v2). Rows have no dividers
 * between them: the row height, the panel edge and the panel head carry the structure. The
 * one accent is --color-brand; green (--color-live) only marks what is running, orange
 * (--color-late) only what is late.
 */

/** A white panel with a hairline edge (`.panel`). */
export const Panel: React.FC<React.HTMLAttributes<HTMLElement> & { as?: 'div' | 'section' }> = ({
  as = 'div',
  className = '',
  children,
  ...rest
}) => React.createElement(as, { className: `ui-panel ${className}`.trim(), ...rest }, children);

interface PanelHeadProps {
  title: React.ReactNode;
  /** A count, a total or an action on the end. */
  trailing?: React.ReactNode;
  /** Makes the head one button (opens a stage, folds a group). */
  onClick?: () => void;
  expanded?: boolean;
  id?: string;
}

/** The grey head of a panel (`.panel-head`): bold title, the count or action on the end. */
export const PanelHead: React.FC<PanelHeadProps> = ({ title, trailing, onClick, expanded, id }) => {
  const content = (
    <>
      <span className="ui-panel-head__title" id={id}>{title}</span>
      {trailing !== undefined && <span className="ui-panel-head__trailing">{trailing}</span>}
    </>
  );
  if (!onClick) return <div className="ui-panel-head">{content}</div>;
  return (
    <button type="button" className="ui-panel-head ui-panel-head--button" onClick={onClick} aria-expanded={expanded}>
      {content}
    </button>
  );
};

interface CheckProps {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  /** Says what tapping does: "Complete “Stretch”". */
  label: string;
  small?: boolean;
}

/** The square check of a task row (`.check`). */
export const Check: React.FC<CheckProps> = ({ checked, onChange, label, small }) => (
  <button
    type="button"
    className={`ui-check${small ? ' ui-check--small' : ''}`}
    aria-pressed={checked}
    aria-label={label}
    disabled={!onChange}
    onClick={(e) => {
      e.stopPropagation();
      onChange?.(!checked);
    }}
  >
    {checked && <Icon name="check" />}
  </button>
);

interface TaskRowProps {
  title: React.ReactNode;
  leading?: React.ReactNode;
  meta?: React.ReactNode;
  /** 'late' paints the meta orange, 'live' green. */
  metaTone?: 'late' | 'live';
  trailing?: React.ReactNode;
  done?: boolean;
  running?: boolean;
  onClick?: () => void;
  /** A drag grip at the end. */
  handle?: boolean;
}

/** One task (`.row`): no divider, a hover ground; the running one tinted green. */
export const TaskRow: React.FC<TaskRowProps> = ({
  title,
  leading,
  meta,
  metaTone,
  trailing,
  done,
  running,
  onClick,
  handle,
}) => {
  const classes = ['ui-row', done && 'is-done', running && 'is-live', onClick && 'is-clickable']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes}>
      {leading}
      {onClick ? (
        <button type="button" className="ui-row__title" onClick={onClick}>
          {title}
        </button>
      ) : (
        <span className="ui-row__title">{title}</span>
      )}
      {meta !== undefined && meta !== null && meta !== '' && (
        <span className={`ui-row__meta${metaTone ? ` is-${metaTone}` : ''}`}>{meta}</span>
      )}
      {trailing}
      {handle && <Icon name="grip" className="ui-row__handle" aria-hidden="true" />}
    </div>
  );
};

export interface TaskGridColumn {
  key: string;
  /** Column label for the head row ("GÖREV", "SÜRE"…); shown as written. */
  label: string;
  /** CSS track, e.g. "minmax(0, 1fr)" or "72px". */
  width: string;
  /** Hidden under this viewport width (px). */
  hideBelow?: number;
}

interface TaskGridProps {
  columns: TaskGridColumn[];
  rows: Array<{ key: string; cells: Record<string, React.ReactNode>; running?: boolean; done?: boolean }>;
  /** Announced name of the grid. */
  label: string;
}

/**
 * The web task table (`.tgrid`): a quiet head row, rows 40px with a soft top line in a low
 * contrast colour (the one place rows have a line, to follow columns across).
 */
export const TaskGrid: React.FC<TaskGridProps> = ({ columns, rows, label }) => {
  const template = columns.map((c) => c.width).join(' ');
  const hide = (c: TaskGridColumn) => (c.hideBelow ? ` ui-grid__cell--hide-${c.hideBelow}` : '');
  return (
    <div className="ui-grid" role="table" aria-label={label}>
      <div className="ui-grid__row ui-grid__row--head" role="row" style={{ gridTemplateColumns: template }}>
        {columns.map((c) => (
          <span key={c.key} role="columnheader" className={`ui-grid__head${hide(c)}`}>
            {c.label}
          </span>
        ))}
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          role="row"
          className={`ui-grid__row${r.running ? ' is-live' : ''}${r.done ? ' is-done' : ''}`}
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((c) => (
            <span key={c.key} role="cell" className={`ui-grid__cell${hide(c)}`}>
              {r.cells[c.key]}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

interface SegmentedProps<T extends string> {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  /** Names the group ("Görünüm"). */
  label: string;
}

/** A two- or three-way switch (`.seg`): a grey track, the chosen one raised in white. */
export function Segmented<T extends string>({ options, value, onChange, label }: SegmentedProps<T>) {
  return (
    <div className="ui-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className="ui-seg__item"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface ChipProps {
  label: string;
  selected?: boolean;
  count?: number;
  onClick?: () => void;
  /** A colour dot before the label (a tag). */
  dot?: string;
}

/** A filter pill (`.chip`): ink on paper when pressed. */
export const Chip: React.FC<ChipProps> = ({ label, selected = false, count, onClick, dot }) => (
  <button type="button" className="ui-chip" aria-pressed={selected} onClick={onClick}>
    {dot && <span className="ui-chip__dot" style={{ background: dot }} aria-hidden="true" />}
    {count === undefined ? label : `${label} · ${count}`}
  </button>
);

/** A 3px progress line (`.progress`), 0–1. */
export const ProgressLine: React.FC<{ value: number; label?: string; done?: boolean }> = ({ value, label, done }) => {
  const pct = Math.round(Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0)) * 100);
  return (
    <span
      className={`ui-progress${done ? ' is-done' : ''}`}
      role={label ? 'progressbar' : undefined}
      aria-label={label}
      aria-valuenow={label ? pct : undefined}
      aria-valuemin={label ? 0 : undefined}
      aria-valuemax={label ? 100 : undefined}
      aria-hidden={label ? undefined : true}
    >
      <span style={{ width: `${pct}%` }} />
    </span>
  );
};

interface AddRowProps {
  label: string;
  placeholder: string;
  onSubmit: (title: string) => void | Promise<unknown>;
  /** Controlled: whether the field is open (a keyboard shortcut opens it from outside). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * The last row of a list (`.add-row`): "+ Görev ekle"; a click turns it into a field. Enter
 * adds and keeps the field for the next one, Escape or leaving the field closes it.
 */
export const AddRow: React.FC<AddRowProps> = ({ label, placeholder, onSubmit, open, onOpenChange }) => {
  const [ownEditing, setOwnEditing] = useState(false);
  const editing = open ?? ownEditing;
  const setEditing = (next: boolean) => {
    setOwnEditing(next);
    onOpenChange?.(next);
  };
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  if (!editing) {
    return (
      <button type="button" className="ui-add-row" onClick={() => setEditing(true)}>
        <span className="ui-add-row__plus" aria-hidden="true">
          <Icon name="plus" />
        </span>
        {label}
      </button>
    );
  }
  return (
    <div className="ui-add-input">
      <input
        ref={inputRef}
        autoFocus
        type="text"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (!value.trim()) setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setValue('');
            setEditing(false);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const text = value.trim();
            if (!text) {
              setEditing(false);
              return;
            }
            void onSubmit(text);
            setValue('');
            inputRef.current?.focus();
          }
        }}
      />
    </div>
  );
};

/** A row of keyboard hints (`.kbd-hint`): "Enter ekle · Esc kapat". */
export const KbdHint: React.FC<{ hints: Array<{ keys: string; label: string }> }> = ({ hints }) => (
  <div className="ui-kbd-hint">
    {hints.map((h) => (
      <span key={h.keys}>
        <kbd>{h.keys}</kbd> {h.label}
      </span>
    ))}
  </div>
);

/** A plain empty state inside a panel: a title, a line, an optional action. */
export const EmptyNote: React.FC<{ title: string; description?: string; action?: React.ReactNode }> = ({
  title,
  description,
  action,
}) => (
  <div className="ui-empty">
    <strong>{title}</strong>
    {description && <span>{description}</span>}
    {action}
  </div>
);
